/**
 * Centralized PBR Material Library
 *
 * The repo still uses one texture set on disk, but the runtime profiles below
 * deliberately split how framing, panel, benches, exterior and ground are lit
 * and tiled so the viewer reads as designed materials rather than one wallpaper.
 *
 * Visible wood surfaces (panel, bench, exterior, stave) include per-board
 * variation via onBeforeCompile shader injection — see woodVariation.ts.
 */

import * as THREE from "three";
import type { KTX2Loader } from "three/addons/loaders/KTX2Loader.js";
import {
	EXTERIOR_TREATMENT_CATALOG,
	INTERIOR_WOOD_CATALOG,
} from "../domain/finishes/finishCatalog";
import { DEFAULT_FINISHES, type FinishConfig, type InteriorWoodSpecies } from "../domain/finishes/types";
import { applyConcreteVariation } from "./concreteVariation";
import { applyEdgeBevel } from "./edgeBevel";
import { applyFeltVariation } from "./feltVariation";
import { applyGlazingFresnel } from "./glazingFresnel";
import { applyGroundVariation } from "./groundVariation";
import {
	EXTERIOR_TEXTURE_PLANKS_PER_TILE,
	GROUND_TEXTURE_PALETTE,
	MATERIAL_TEXTURE_PROFILES,
	PRESENTATION_MATERIAL_KEYS,
} from "./materialProfiles";
import { applyTimberGrain } from "./timberGrain";
import { applyWoodVariation, type WoodVariationParams } from "./woodVariation";

export {
	GROUND_TEXTURE_PALETTE as GROUND_TEXTURE_PROFILE,
	MATERIAL_TEXTURE_PROFILES,
	PRESENTATION_MATERIAL_KEYS,
};

export const MATERIAL_TEXTURE_ASSETS = {
	panel: {
		albedo: {
			fallback: "/assets/textures/aspen-panel-albedo.jpeg",
			ktx2: "/assets/textures/aspen-panel-albedo.ktx2",
			swatch: 0xe5d7c1,
		},
		normal: {
			fallback: "/assets/textures/wood_normal.jpg",
			ktx2: "/assets/textures/wood_normal.ktx2",
		},
		roughness: {
			fallback: "/assets/textures/aspen-wood-roughness.jpeg",
			ktx2: "/assets/textures/aspen-wood-roughness.ktx2",
		},
	},
	bench: {
		albedo: {
			fallback: "/assets/textures/aspen-bench-albedo.jpeg",
			ktx2: "/assets/textures/aspen-bench-albedo.ktx2",
			swatch: 0xdcc9b1,
		},
		normal: {
			fallback: "/assets/textures/wood_normal.jpg",
			ktx2: "/assets/textures/wood_normal.ktx2",
		},
		roughness: {
			fallback: "/assets/textures/aspen-wood-roughness.jpeg",
			ktx2: "/assets/textures/aspen-wood-roughness.ktx2",
		},
	},
	exterior: {
		albedo: {
			fallback: "/assets/textures/spruce-exterior-albedo.jpeg",
			ktx2: "/assets/textures/spruce-exterior-albedo.ktx2",
			swatch: 0xbd9c79,
		},
		normal: {
			fallback: "/assets/textures/spruce-exterior-normal.jpeg",
			ktx2: "/assets/textures/spruce-exterior-normal.ktx2",
		},
		roughness: {
			fallback: "/assets/textures/spruce-exterior-roughness.jpeg",
			ktx2: "/assets/textures/spruce-exterior-roughness.ktx2",
		},
	},
	/**
	 * Single-plank crops of the `exterior` set for trim boards (knutbrädor,
	 * foder, sockel, fascia). Trim used to UV-window the multi-plank sheet
	 * to a plank interior, but a UV window only keeps seams off the face at
	 * mip 0 — every mip level is an average of the WHOLE sheet, so heavily
	 * minified trim faces (the 22 mm edge reveals, the sockel's top ledge)
	 * bled the plank seams back in as a dark/rusty line along the board.
	 * A cropped source keeps every mip seam-free by construction.
	 *
	 * Deliberately NO ktx2 variants: ETC1S block compression on this narrow
	 * (232px) crop turns the heavily minified trim faces — the sockel's top
	 * ledge, the knutbräda edge reveals — into rust-coloured speckle noise.
	 * The jpegs are small and trim covers little screen area, so the
	 * uncompressed upload cost is negligible.
	 */
	exteriorPlank: {
		albedo: {
			fallback: "/assets/textures/spruce-exterior-plank-albedo.jpeg",
		},
		normal: {
			fallback: "/assets/textures/spruce-exterior-plank-normal.jpeg",
		},
		roughness: {
			fallback: "/assets/textures/spruce-exterior-plank-roughness.jpeg",
		},
	},
	ground: {
		albedo: "/assets/textures/grass-albedo.webp",
		normal: "/assets/textures/grass-normal.webp",
		roughness: "/assets/textures/grass-roughness.webp",
	},
} as const;

const FRAMING_WOOD_TEXTURE_ASSETS = {
	/**
	 * Deliberately NO ktx2 variant, same as `exteriorPlank` but for a different
	 * reason: `wood_diffuse.ktx2` does not render equivalently to its jpeg. The
	 * barrel's staves use this slot over the whole model and came out flat pale
	 * tan with no grain instead of dark figured wood — 19% of the canonical
	 * barrel baseline.
	 *
	 * It went unnoticed for as long as it existed because the upgrade was
	 * effectively dead: `tryApplyCompressedTexture` bailed out whenever a
	 * material was constructed before `configureCompressedTextureSupport` had
	 * resolved, which is the common case, so the jpeg simply stayed. Making the
	 * binding deferred (rather than best-effort) activated the upgrade for the
	 * first time and exposed the asset.
	 *
	 * `normal` and `roughness` were verified good and keep their ktx2 — only
	 * the albedo is bad. Restore this entry once the asset is regenerated AND
	 * the barrel baseline passes with it.
	 */
	albedo: {
		fallback: "/assets/textures/wood_diffuse.jpg",
	},
	normal: {
		fallback: "/assets/textures/wood_normal.jpg",
		ktx2: "/assets/textures/wood_normal.ktx2",
	},
	roughness: {
		fallback: "/assets/textures/wood_roughness.jpg",
		ktx2: "/assets/textures/wood_roughness.ktx2",
	},
} as const;

type MaterialProfileKey = keyof typeof MATERIAL_TEXTURE_PROFILES;
type WoodProfileKey =
	| "framing"
	| "panel"
	| "bench"
	| "exterior"
	| "stave"
	| "roof";

/**
 * UV transform baked into a cached texture instance. Textures are shared
 * by cache key, so a transform MUST be part of the key — otherwise
 * rotating e.g. the fascia's map would rotate the wall cladding too.
 */
interface TextureTransform {
	rotation: number;
	repeat: [number, number];
	offset: [number, number];
}

interface TextureRequest {
	path: string;
	data?: boolean;
	profile?: MaterialProfileKey;
	transform?: TextureTransform;
}

interface TextureAsset {
	fallback: string;
	ktx2?: string;
	/**
	 * The albedo's average colour (sRGB), measured from the `fallback` image.
	 * Bound as a 1x1 stand-in map while the ktx2 is still on its way, so the
	 * surface renders as the wood it will become rather than as its bare
	 * `color`. The profile tints are multipliers tuned for the textured
	 * surface: the exterior's 0xd6cab8 alone reads as pale grey, which is
	 * how untreated spruce came to look like järnvitriol whenever the
	 * cladding transcode was slow.
	 */
	swatch?: number;
}

interface TextureSlotAssets {
	map?: TextureAsset | string;
	normalMap?: TextureAsset | string;
	roughnessMap?: TextureAsset | string;
}

interface MaterialState<T extends THREE.Material> {
	get: () => T;
	dispose: () => void;
}

const FRAMING_TEXTURE_SLOTS = {
	map: FRAMING_WOOD_TEXTURE_ASSETS.albedo,
	normalMap: FRAMING_WOOD_TEXTURE_ASSETS.normal,
	roughnessMap: FRAMING_WOOD_TEXTURE_ASSETS.roughness,
} as const satisfies TextureSlotAssets;

const PANEL_TEXTURE_SLOTS = {
	map: MATERIAL_TEXTURE_ASSETS.panel.albedo,
	normalMap: MATERIAL_TEXTURE_ASSETS.panel.normal,
	roughnessMap: MATERIAL_TEXTURE_ASSETS.panel.roughness,
} as const satisfies TextureSlotAssets;

const BENCH_TEXTURE_SLOTS = {
	map: MATERIAL_TEXTURE_ASSETS.bench.albedo,
	normalMap: MATERIAL_TEXTURE_ASSETS.bench.normal,
	roughnessMap: MATERIAL_TEXTURE_ASSETS.bench.roughness,
} as const satisfies TextureSlotAssets;

const EXTERIOR_TEXTURE_SLOTS = {
	map: MATERIAL_TEXTURE_ASSETS.exterior.albedo,
	normalMap: MATERIAL_TEXTURE_ASSETS.exterior.normal,
	roughnessMap: MATERIAL_TEXTURE_ASSETS.exterior.roughness,
} as const satisfies TextureSlotAssets;

const EXTERIOR_PLANK_TEXTURE_SLOTS = {
	map: MATERIAL_TEXTURE_ASSETS.exteriorPlank.albedo,
	normalMap: MATERIAL_TEXTURE_ASSETS.exteriorPlank.normal,
	roughnessMap: MATERIAL_TEXTURE_ASSETS.exteriorPlank.roughness,
} as const satisfies TextureSlotAssets;

const textureCache = new Map<string, THREE.Texture>();
const compressedTexturePending = new Map<
	string,
	Promise<THREE.Texture | null>
>();
/** In-flight/settled ktx2 transcodes keyed by URL, cloned per cache key. */
const compressedSourcePending = new Map<
	string,
	Promise<THREE.CompressedTexture | null>
>();
const materialStates: Array<MaterialState<THREE.Material>> = [];
let textureLoader: THREE.TextureLoader | null = null;
/**
 * In-flight jpeg/webp fallback decodes, tracked for `hasPendingTextureWork`.
 *
 * A Set of tokens rather than a counter: `disposeMaterialLibrary` clears it
 * while loads may still be outstanding, and a stale `settle` deleting a token
 * that is already gone is a no-op — whereas decrementing a counter would drive
 * it negative and mask the NEXT load.
 */
const pendingFallbackLoads = new Set<object>();
let ktx2Loader: KTX2Loader | null = null;
let materialLibraryInvalidate: (() => void) | null = null;

/**
 * Whether this renderer can consume the `.ktx2` variants.
 *
 * `unknown` until `configureCompressedTextureSupport` has run — it needs the
 * live renderer to probe the compressed-texture extensions, and it is called
 * from the Canvas `onCreated`, which can land either side of the first
 * material construction.
 *
 * This gates whether the `.fallback` jpeg is fetched AT ALL. Loading both used
 * to cost ~1.2 MB of redundant bytes that competed for bandwidth with the very
 * ktx2 files meant to replace them — on a real production load the discarded
 * jpegs were the LAST requests to land (~35 s), pushing the cladding they were
 * already superseded by out to ~23 s. A slot with a ktx2 variant therefore
 * starts empty and is bound once support is known.
 */
type CompressedSupport = "unknown" | "yes" | "no";
let compressedSupport: CompressedSupport = "unknown";

/**
 * Slot bindings parked while `compressedSupport` is still `unknown`. Flushed
 * exactly once, from `configureCompressedTextureSupport`. Anything parked here
 * is a material with an unpopulated texture slot, so the flush is not optional.
 */
const deferredSlotBindings: Array<() => void> = [];

function flushDeferredSlotBindings() {
	const pending = deferredSlotBindings.splice(0, deferredSlotBindings.length);
	for (const bind of pending) {
		bind();
	}
}

/** Environments where KTX2Loader can run at all (needs DOM + Worker). */
function canUseCompressedTextures() {
	return typeof document !== "undefined" && typeof Worker !== "undefined";
}

/**
 * Profiles whose textures are only visible INSIDE the sauna, and whose ktx2
 * downloads are therefore held back until the exterior shell has finished.
 *
 * HTTP/2 multiplexes rather than prioritises: issuing every request at once
 * means they all share the pipe and everything finishes late together. The
 * cladding is what the user is looking at on the default camera, so the ~1.2 MB
 * unique to these two profiles (aspen-panel-albedo, aspen-wood-roughness,
 * aspen-bench-albedo) waits. `wood_normal` is shared with `framing` and loads
 * eagerly regardless.
 *
 * KTX2Loader also runs `setWorkerLimit(1)`, so transcoding is serial anyway —
 * ordering the requests costs nothing and buys the shell an earlier finish.
 */
const INTERIOR_DEFERRED_PROFILES = new Set<MaterialProfileKey>([
	"panel",
	"bench",
]);

/**
 * Hard cap on how long interior textures wait for the shell. Without it a
 * single stalled exterior request would leave the interior flat-tinted for the
 * whole session — a slow shell must degrade into "everything loads", never into
 * "the interior never loads".
 */
const INTERIOR_TEXTURE_MAX_WAIT_MS = 3000;

/** Bindings waiting on the shell; null once the gate has opened for good. */
let interiorBindingQueue: Array<() => void> | null = [];

function releaseInteriorBindings() {
	const queued = interiorBindingQueue;
	interiorBindingQueue = null;
	if (!queued) return;
	for (const bind of queued) {
		bind();
	}
}

/**
 * Open the interior gate once every exterior/framing transcode has settled, or
 * when the cap expires — whichever comes first.
 */
function scheduleInteriorRelease() {
	if (interiorBindingQueue === null) return;

	const timer = setTimeout(
		releaseInteriorBindings,
		INTERIOR_TEXTURE_MAX_WAIT_MS,
	);
	// `compressedTexturePending` holds exactly the shell requests at this point:
	// interior bindings have not run, so they have not enqueued anything yet.
	void Promise.allSettled([...compressedTexturePending.values()]).then(() => {
		clearTimeout(timer);
		releaseInteriorBindings();
	});
}

/**
 * True while any texture slot is still waiting to be populated — a compressed
 * transcode in flight, a binding parked on the support probe, or an interior
 * binding held behind the shell.
 *
 * Exists for `ViewerFrameReadySignal`. Skipping the jpeg fallback means a
 * material now renders with NO map at all until its ktx2 lands, instead of
 * showing the jpeg immediately; a "4 consecutive painted frames" readiness
 * check therefore stops implying "textured", and the visual harness captured
 * flat-tinted surfaces. This is the difference between a partially-loaded
 * frame and a complete one, which is exactly what that signal gates on.
 */
export function hasPendingTextureWork(): boolean {
	return (
		pendingFallbackLoads.size > 0 ||
		compressedTexturePending.size > 0 ||
		deferredSlotBindings.length > 0 ||
		(interiorBindingQueue?.length ?? 0) > 0
	);
}

/**
 * True when the jpeg fallback for `asset` should be skipped because a ktx2
 * variant exists and compressed support is either confirmed or still pending.
 * Assets without a ktx2 variant (exteriorPlank, ground) always load eagerly.
 */
function deferToCompressedVariant(asset: TextureAsset | string): boolean {
	if (compressedSupport === "no" || !canUseCompressedTextures()) return false;
	return Boolean(resolveTextureAsset(asset).ktx2);
}

function getTextureLoader() {
	textureLoader ??= new THREE.TextureLoader();
	return textureLoader;
}

function applyTextureProfile(
	texture: THREE.Texture,
	profile: { repeat: [number, number]; anisotropy: number },
) {
	texture.repeat.set(profile.repeat[0], profile.repeat[1]);
	texture.anisotropy = profile.anisotropy;
	return texture;
}

function configureTexture(
	texture: THREE.Texture,
	request: Required<Pick<TextureRequest, "data">> & TextureRequest,
	markReady = true,
) {
	texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
	texture.colorSpace = request.data
		? THREE.LinearSRGBColorSpace
		: THREE.SRGBColorSpace;
	if (request.profile) {
		applyTextureProfile(texture, MATERIAL_TEXTURE_PROFILES[request.profile]);
	}
	if (request.transform) {
		texture.center.set(0.5, 0.5);
		texture.rotation = request.transform.rotation;
		texture.repeat.set(...request.transform.repeat);
		texture.offset.set(...request.transform.offset);
	}
	if (markReady) {
		texture.needsUpdate = true;
	}
	return texture;
}

function getTextureCacheKey({
	path,
	data = false,
	profile,
	transform,
}: TextureRequest) {
	const transformKey = transform
		? `rot${transform.rotation}|rep${transform.repeat.join(",")}|off${transform.offset.join(",")}`
		: "identity";
	return `${path}|${data ? "data" : "color"}|${profile ?? "none"}|${transformKey}`;
}

const SWATCH_PLACEHOLDER = "swatchPlaceholder";

/** True for the 1x1 stand-in `loadTextureSlots` binds ahead of a ktx2. */
export function isSwatchPlaceholder(
	texture: THREE.Texture | null | undefined,
): boolean {
	return texture?.userData[SWATCH_PLACEHOLDER] === true;
}

function loadSwatchTexture(swatch: number): THREE.Texture {
	const cacheKey = `swatch|${swatch.toString(16).padStart(6, "0")}`;
	const existing = textureCache.get(cacheKey);
	if (existing) return existing;
	const texture = new THREE.DataTexture(
		new Uint8Array([
			(swatch >> 16) & 0xff,
			(swatch >> 8) & 0xff,
			swatch & 0xff,
			255,
		]),
		1,
		1,
	);
	texture.colorSpace = THREE.SRGBColorSpace;
	texture.userData[SWATCH_PLACEHOLDER] = true;
	texture.name = cacheKey;
	texture.needsUpdate = true;
	textureCache.set(cacheKey, texture);
	return texture;
}

function resolveTextureAsset(asset: TextureAsset | string): TextureAsset {
	return typeof asset === "string" ? { fallback: asset } : asset;
}

function loadTexture(
	path: string,
	{ data = false, profile, transform }: Omit<TextureRequest, "path"> = {},
): THREE.Texture {
	const request: Required<Pick<TextureRequest, "data">> & TextureRequest = {
		path,
		data,
		profile,
		transform,
	};
	const cacheKey = getTextureCacheKey(request);
	const existing = textureCache.get(cacheKey);
	if (existing) {
		return existing;
	}

	let texture: THREE.Texture;
	if (typeof document === "undefined") {
		texture = new THREE.Texture();
	} else {
		// Counted in `hasPendingTextureWork`: a jpeg decode is asynchronous too,
		// and a fallback-only slot (wood_diffuse albedo, exteriorPlank) has no
		// transcode to stand in for it. Incremented BEFORE `load` because
		// THREE.Cache can invoke `onLoad` synchronously.
		const token = {};
		pendingFallbackLoads.add(token);
		const settle = () => {
			pendingFallbackLoads.delete(token);
		};
		texture = getTextureLoader().load(
			path,
			(loaded) => {
				settle();
				configureTexture(loaded, request);
				// Demand frameloop: the arriving image is not painted otherwise.
				materialLibraryInvalidate?.();
			},
			undefined,
			settle,
		);
	}

	configureTexture(texture, request, typeof document === "undefined");
	texture.name = cacheKey;
	textureCache.set(cacheKey, texture);
	return texture;
}

function loadTextureAsset(
	asset: TextureAsset | string,
	options: Omit<TextureRequest, "path"> = {},
): THREE.Texture {
	return loadTexture(resolveTextureAsset(asset).fallback, options);
}

function tryApplyCompressedTexture(
	material: THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial,
	slot: "map" | "normalMap" | "roughnessMap",
	asset: TextureAsset | string,
	options: Omit<TextureRequest, "path">,
) {
	const resolved = resolveTextureAsset(asset);
	if (
		!resolved.ktx2 ||
		!ktx2Loader ||
		typeof document === "undefined" ||
		typeof Worker === "undefined"
	) {
		return;
	}

	const request: Required<Pick<TextureRequest, "data">> & TextureRequest = {
		path: resolved.ktx2,
		data: options.data ?? false,
		profile: options.profile,
		transform: options.transform,
	};
	const cacheKey = getTextureCacheKey(request);
	const existing = textureCache.get(cacheKey);
	if (existing) {
		material[slot] = existing;
		material.needsUpdate = true;
		return;
	}

	// One transcode per URL, kept for the session. KTX2Loader's FileLoader only
	// merges requests that are in flight AT THE SAME TIME; once the interior
	// tier was held back behind the shell, `wood_normal.ktx2` (shared by the
	// framing and panel/bench profiles, under different cache keys) stopped
	// overlapping and became a second network round trip.
	let source = compressedSourcePending.get(resolved.ktx2);
	if (!source) {
		source = ktx2Loader.loadAsync(resolved.ktx2).catch(() => null);
		compressedSourcePending.set(resolved.ktx2, source);
	}

	let pending = compressedTexturePending.get(cacheKey);
	if (!pending) {
		pending = source
			.then((shared) => {
				if (!shared) return null;
				// KTX2Loader's `_taskCache` returns the SAME CompressedTexture
				// for a given buffer. Configuring that shared instance would
				// leak this request's UV transform onto every other cache key
				// using the URL — the wall cladding rendered with the
				// horizontal trim's 90° rotation whenever its configure ran
				// last. Clone per cache key (clone shares the transcoded mip
				// data, so no re-transcode) and configure the private instance.
				const texture = shared.clone();
				configureTexture(texture, request);
				texture.name = cacheKey;
				textureCache.set(cacheKey, texture);
				return texture;
			})
			.catch(() => null)
			.finally(() => {
				compressedTexturePending.delete(cacheKey);
			});
		compressedTexturePending.set(cacheKey, pending);
	}

	pending.then((texture) => {
		if (!texture) {
			// The transcode failed (404, corrupt payload, transcoder init
			// error). `loadTextureSlots` deliberately skipped this slot's jpeg
			// because a ktx2 variant existed, so without this the slot stays
			// empty for the rest of the session and the surface renders as a
			// flat tint. `compressedSourcePending` keeps the rejected entry
			// cached on purpose: every later material for this URL resolves
			// null immediately and lands here, rather than re-issuing a request
			// that just failed.
			applyFallbackTexture(material, slot, asset, options);
			return;
		}
		material[slot] = texture;
		material.needsUpdate = true;
		materialLibraryInvalidate?.();
	});
}

/** Assign the jpeg fallback, unless the slot is already populated. */
function applyFallbackTexture(
	material: THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial,
	slot: "map" | "normalMap" | "roughnessMap",
	asset: TextureAsset | string,
	options: Omit<TextureRequest, "path">,
) {
	if (material[slot] && !isSwatchPlaceholder(material[slot])) return;
	material[slot] = loadTextureAsset(asset, options);
	material.needsUpdate = true;
	materialLibraryInvalidate?.();
}

/**
 * Populate one slot once `compressedSupport` is settled. Two outcomes:
 * - `yes` → transcode the ktx2 (slot was left empty by `loadTextureSlots`).
 * - `no`  → the ktx2 never arrives, so load the jpeg fallback we skipped.
 *
 * The `material[slot]` guard keeps this a no-op for slots that already have
 * their fallback (assets with no ktx2 variant), so it is safe either way.
 */
function bindTextureSlot(
	material: THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial,
	slot: "map" | "normalMap" | "roughnessMap",
	asset: TextureAsset | string,
	options: Omit<TextureRequest, "path">,
) {
	if (compressedSupport === "yes") {
		tryApplyCompressedTexture(material, slot, asset, options);
		return;
	}
	applyFallbackTexture(material, slot, asset, options);
	materialLibraryInvalidate?.();
}

function bindWoodMaterialTextures(
	material: THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial,
	assets: TextureSlotAssets,
	profileKey: MaterialProfileKey,
	transform?: TextureTransform,
) {
	const bind = () => {
		if (assets.map) {
			bindTextureSlot(material, "map", assets.map, {
				profile: profileKey,
				transform,
			});
		}
		if (assets.normalMap) {
			bindTextureSlot(material, "normalMap", assets.normalMap, {
				data: true,
				profile: profileKey,
				transform,
			});
		}
		if (assets.roughnessMap) {
			bindTextureSlot(material, "roughnessMap", assets.roughnessMap, {
				data: true,
				profile: profileKey,
				transform,
			});
		}
	};

	// Interior profiles queue behind the shell — but ONLY when they would
	// actually issue a compressed download. With support absent the bind is a
	// cheap jpeg assignment and gating it would just delay the interior for
	// nothing, on the very renderers that are already slowest.
	const gated = () => {
		if (
			compressedSupport === "yes" &&
			interiorBindingQueue !== null &&
			INTERIOR_DEFERRED_PROFILES.has(profileKey)
		) {
			interiorBindingQueue.push(bind);
			return;
		}
		bind();
	};

	if (compressedSupport === "unknown" && canUseCompressedTextures()) {
		deferredSlotBindings.push(gated);
		return;
	}
	gated();
}

/**
 * Bumped by `disposeMaterialLibrary()`. Dynamic `import()` cannot be
 * cancelled, so an async continuation started before a dispose can still
 * resume after it. Without this guard, a canvas that unmounts before the
 * KTX2Loader import settles (a quick route change, React StrictMode's effect
 * replay) would let its now-stale `.then` recreate/mutate the shared
 * `ktx2Loader` and start `.init()` — a wasm fetch plus an un-revoked worker
 * Blob URL — with no mounted canvas left to ever call `.dispose()` on it.
 * Every continuation that survives an `await` here captures the generation
 * beforehand and re-checks it on resume.
 */
let materialLibraryGeneration = 0;

/**
 * Shared in-flight warm-up so an early `prewarmCompressedTextureTranscoder`
 * and the Canvas `onCreated` `configureCompressedTextureSupport` cooperate on
 * ONE import + `init()` instead of racing two.
 */
let ktx2LoaderWarmup: Promise<KTX2Loader | null> | null = null;

/**
 * Import the KTX2Loader and start compiling the basis transcoder as early as
 * the viewer can — call it the instant the scene canvas mounts, BEFORE the
 * first `buildSauna3D` and its shader compiles run.
 *
 * Why it matters: `configureCompressedTextureSupport` cannot report
 * `compressedSupport = "yes"` — and therefore cannot release the (already
 * queued) ktx2 texture bindings — until this dynamic `import()` resolves. On a
 * heavy scene under headless SwiftShader the import's module evaluation gets
 * starved behind the synchronous scene build + ~48 shader compiles: measured on
 * square-canonical the import did not resolve until ~30 s, holding every ktx2
 * transcode (and thus the whole textured, stable frame) behind it. Kicking the
 * import off at mount, in the quiet window before the build, drops that to
 * <1 s and starts the transcodes ~26 s earlier. Idempotent; renderer-free
 * (`detectSupport` still needs the live renderer and stays below).
 *
 * Resolves to `null` — without touching the shared `ktx2Loader` — if
 * `disposeMaterialLibrary()` ran while the import was in flight; see
 * `materialLibraryGeneration`.
 */
export function prewarmCompressedTextureTranscoder(): Promise<KTX2Loader | null> {
	if (ktx2Loader) return Promise.resolve(ktx2Loader);
	if (ktx2LoaderWarmup) return ktx2LoaderWarmup;
	if (typeof document === "undefined" || typeof Worker === "undefined") {
		return Promise.resolve(null);
	}
	const generation = materialLibraryGeneration;
	ktx2LoaderWarmup = import("three/addons/loaders/KTX2Loader.js")
		.then(({ KTX2Loader }) => {
			if (generation !== materialLibraryGeneration) return null;
			ktx2Loader ??= new KTX2Loader()
				.setTranscoderPath("/assets/basis/")
				.setWorkerLimit(1);
			// Start fetching+compiling basis_transcoder.js/.wasm (~267 kB) now
			// rather than on first loadAsync, where it would queue behind the
			// texture downloads it is required to decode.
			void ktx2Loader.init().catch(() => {});
			return ktx2Loader;
		})
		.catch(() => null);
	return ktx2LoaderWarmup;
}

export async function configureCompressedTextureSupport(
	renderer: THREE.WebGLRenderer,
	onTextureReady?: () => void,
) {
	if (typeof document === "undefined") return;
	// Every early return below must still settle `compressedSupport` and flush,
	// or materials parked on the deferred queue keep an empty texture slot.
	if (
		!(
			(renderer as THREE.WebGLRenderer & { isWebGPURenderer?: boolean })
				.isWebGPURenderer === true
		) &&
		typeof renderer.extensions?.has !== "function"
	) {
		compressedSupport = "no";
		flushDeferredSlotBindings();
		return;
	}
	materialLibraryInvalidate = onTextureReady ?? null;
	if (typeof Worker === "undefined") {
		compressedSupport = "no";
		flushDeferredSlotBindings();
		return;
	}
	const generation = materialLibraryGeneration;
	try {
		// Reuse (or start) the shared warm-up. When the viewer prewarmed it at
		// canvas mount this resolves immediately; otherwise it does the import
		// here as before. Either way this is the ONLY import+init path.
		const warmed = await prewarmCompressedTextureTranscoder();
		if (generation !== materialLibraryGeneration) {
			// A dispose happened while the import was in flight, so this call
			// belongs to a torn-down canvas — a newer generation's own
			// `configureCompressedTextureSupport` call now owns `ktx2Loader` /
			// `compressedSupport` / the deferred-binding flush. Bail without
			// touching any of it: calling `detectSupport` here would configure
			// a (possibly shared, possibly already-live) loader with a
			// torn-down renderer's extensions.
			return;
		}
		if (!warmed) throw new Error("ktx2 transcoder unavailable");
		warmed.detectSupport(renderer);
		compressedSupport = "yes";
	} catch {
		ktx2Loader = null;
		compressedSupport = "no";
	}
	// Order matters: the flush issues the shell's ktx2 requests, and
	// scheduleInteriorRelease reads exactly those out of compressedTexturePending.
	flushDeferredSlotBindings();
	if (compressedSupport === "yes") {
		scheduleInteriorRelease();
	} else {
		// Nothing will ever queue, so don't leave the gate half-closed for
		// materials constructed later.
		releaseInteriorBindings();
	}
}

/** Profile keys that receive per-board wood variation. */
const VARIED_PROFILES = new Set<WoodProfileKey>([
	"framing",
	"panel",
	"exterior",
	"stave",
]);

function createWoodMaterial(
	profileKey: WoodProfileKey,
	textures: {
		map?: THREE.Texture;
		normalMap?: THREE.Texture;
		roughnessMap?: THREE.Texture;
	},
	overrides: Partial<THREE.MeshStandardMaterialParameters> = {},
	variation?: WoodVariationParams,
) {
	const profile = MATERIAL_TEXTURE_PROFILES[profileKey];
	const material = new THREE.MeshStandardMaterial({
		...textures,
		roughness: profile.roughness,
		color: profile.tint,
		normalScale: new THREE.Vector2(...profile.normalScale),
		envMapIntensity: profile.envMapIntensity,
		...overrides,
	});

	if (VARIED_PROFILES.has(profileKey)) {
		applyWoodVariation(material, { ...profile.variation, ...variation });
	}

	// Edge AO stacks cleanly on top of wood-variation — different
	// shader chunks, no overlap. Reads as a 1–2 px micro-chamfer
	// where two faces meet. See edgeBevel.ts for the rationale.
	applyEdgeBevel(material);
	if (profileKey === "bench") applyTimberGrain(material);

	return material;
}

function createPhysicalWoodMaterial(
	profileKey: WoodProfileKey,
	textures: {
		map?: THREE.Texture;
		normalMap?: THREE.Texture;
		roughnessMap?: THREE.Texture;
	},
	overrides: Partial<THREE.MeshPhysicalMaterialParameters> = {},
	variation?: WoodVariationParams,
) {
	const profile = MATERIAL_TEXTURE_PROFILES[profileKey];
	const material = new THREE.MeshPhysicalMaterial({
		...textures,
		roughness: profile.roughness,
		color: profile.tint,
		normalScale: new THREE.Vector2(...profile.normalScale),
		envMapIntensity: profile.envMapIntensity,
		...overrides,
	});

	if (VARIED_PROFILES.has(profileKey)) {
		applyWoodVariation(material, { ...profile.variation, ...variation });
	}

	applyEdgeBevel(material);
	if (profileKey === "bench") applyTimberGrain(material);

	return material;
}

function loadTextureSlots(
	assets: TextureSlotAssets,
	profileKey: MaterialProfileKey,
	transform?: TextureTransform,
) {
	// A slot with a ktx2 variant is left empty here and populated by
	// bindWoodMaterialTextures — fetching the jpeg too would double the bytes
	// for a texture we are about to throw away. Adding the map later costs one
	// shader recompile (needsUpdate), which is what the upgrade path already did.
	//
	// Deferred slots are OMITTED, not set to undefined: the result is spread
	// into the material constructor, and THREE warns loudly for every
	// explicitly-undefined parameter it is handed.
	const slots: {
		map?: THREE.Texture;
		normalMap?: THREE.Texture;
		roughnessMap?: THREE.Texture;
	} = {};

	if (assets.map && !deferToCompressedVariant(assets.map)) {
		slots.map = loadTextureAsset(assets.map, {
			profile: profileKey,
			transform,
		});
	} else if (assets.map) {
		const { swatch } = resolveTextureAsset(assets.map);
		if (swatch !== undefined) slots.map = loadSwatchTexture(swatch);
	}
	if (assets.normalMap && !deferToCompressedVariant(assets.normalMap)) {
		slots.normalMap = loadTextureAsset(assets.normalMap, {
			data: true,
			profile: profileKey,
			transform,
		});
	}
	if (assets.roughnessMap && !deferToCompressedVariant(assets.roughnessMap)) {
		slots.roughnessMap = loadTextureAsset(assets.roughnessMap, {
			data: true,
			profile: profileKey,
			transform,
		});
	}
	return slots;
}

function createTexturedWoodMaterial(
	profileKey: WoodProfileKey,
	assets: TextureSlotAssets,
	overrides: Partial<THREE.MeshStandardMaterialParameters> = {},
	options: {
		transform?: TextureTransform;
		variation?: WoodVariationParams;
	} = {},
) {
	const material = createWoodMaterial(
		profileKey,
		loadTextureSlots(assets, profileKey, options.transform),
		overrides,
		options.variation,
	);
	bindWoodMaterialTextures(material, assets, profileKey, options.transform);
	return material;
}

function createTexturedPhysicalWoodMaterial(
	profileKey: WoodProfileKey,
	assets: TextureSlotAssets,
	overrides: Partial<THREE.MeshPhysicalMaterialParameters> = {},
	options: {
		transform?: TextureTransform;
		variation?: WoodVariationParams;
	} = {},
) {
	const material = createPhysicalWoodMaterial(
		profileKey,
		loadTextureSlots(assets, profileKey, options.transform),
		overrides,
		options.variation,
	);
	bindWoodMaterialTextures(material, assets, profileKey, options.transform);
	return material;
}

function createTexturedStandardMaterial(
	profileKey: MaterialProfileKey,
	assets: TextureSlotAssets,
	parameters: THREE.MeshStandardMaterialParameters,
	bevelOptions?: Parameters<typeof applyEdgeBevel>[1],
	textureTransform?: TextureTransform,
) {
	const material = new THREE.MeshStandardMaterial({
		...loadTextureSlots(assets, profileKey, textureTransform),
		...parameters,
	});
	bindWoodMaterialTextures(material, assets, profileKey, textureTransform);
	applyEdgeBevel(material, bevelOptions);
	return material;
}

/**
 * The karaktärsval currently in force (issue 134).
 *
 * Module-level, like `compressedSupport`, and for the same reason: materials
 * are lazy singletons, so a choice has to survive being made either BEFORE a
 * material exists (the factory reads it) or AFTER (`applyFinishSelection`
 * restyles the live instance). One design is on screen at a time, so a single
 * value is the whole state.
 */
let activeFinishes: FinishConfig = DEFAULT_FINISHES;

/**
 * Materials that restyle themselves when the finish changes.
 *
 * `peek` returns the instance ONLY if it has been built — applying a finish
 * must not instantiate the whole library (and with it every texture download)
 * just because someone clicked a swatch.
 */
const finishAwareMaterials: Array<{
	peek: () => THREE.Material | null;
	apply: (material: THREE.Material, finishes: FinishConfig) => void;
}> = [];

/**
 * Restyle every built wood/facade material for `finishes`.
 *
 * Deliberately a mutation of the shared instances rather than a material
 * pair-swap: a tint is not a different surface, and swapping would multiply
 * the pair table by three species × five colours while fighting the
 * quality-tier pairs over the same meshes. Returns true when something
 * changed, so the viewer can skip the invalidate on a no-op.
 */
export function applyFinishSelection(finishes: FinishConfig): boolean {
	const unchanged =
		activeFinishes.interiorWood === finishes.interiorWood &&
		activeFinishes.exteriorTreatment === finishes.exteriorTreatment;
	activeFinishes = finishes;
	if (unchanged) return false;

	for (const entry of finishAwareMaterials) {
		const material = entry.peek();
		if (material) entry.apply(material, finishes);
	}
	return true;
}

/** The finish the library is currently styled for — for tests and the viewer. */
export function getActiveFinishSelection(): FinishConfig {
	return activeFinishes;
}

function createLazyMaterial<T extends THREE.Material>(
	factory: () => T,
	/**
	 * Restyle hook. Called at construction with the active finish and again
	 * from `applyFinishSelection` whenever the choice changes, so a material
	 * built before OR after the click ends up in the same state.
	 */
	applyFinish?: (material: T, finishes: FinishConfig) => void,
): T {
	let material: T | null = null;
	const state: MaterialState<T> = {
		get() {
			if (!material) {
				material = factory();
				applyFinish?.(material, activeFinishes);
			}
			return material;
		},
		dispose() {
			material?.dispose();
			material = null;
		},
	};
	materialStates.push(state as MaterialState<THREE.Material>);
	if (applyFinish) {
		finishAwareMaterials.push({
			peek: () => material,
			apply: (instance, finishes) => applyFinish(instance as T, finishes),
		});
	}

	return new Proxy({} as T, {
		get(_target, prop, receiver) {
			return Reflect.get(state.get(), prop, receiver);
		},
		set(_target, prop, value, receiver) {
			return Reflect.set(state.get(), prop, value, receiver);
		},
		has(_target, prop) {
			return prop in state.get();
		},
		ownKeys() {
			return Reflect.ownKeys(state.get());
		},
		getOwnPropertyDescriptor(_target, prop) {
			const descriptor = Reflect.getOwnPropertyDescriptor(state.get(), prop);
			return descriptor ? { ...descriptor, configurable: true } : undefined;
		},
		defineProperty(_target, prop, descriptor) {
			return Reflect.defineProperty(state.get(), prop, descriptor);
		},
		deleteProperty(_target, prop) {
			return Reflect.deleteProperty(state.get(), prop);
		},
		getPrototypeOf() {
			return Reflect.getPrototypeOf(state.get());
		},
		setPrototypeOf(_target, prototype) {
			return Reflect.setPrototypeOf(state.get(), prototype);
		},
		isExtensible() {
			return Reflect.isExtensible(state.get());
		},
		preventExtensions() {
			return Reflect.preventExtensions(state.get());
		},
	});
}

export function disposeMaterialLibrary() {
	for (const state of materialStates) {
		state.dispose();
	}

	for (const texture of textureCache.values()) {
		texture.dispose();
	}
	textureCache.clear();
	compressedTexturePending.clear();
	compressedSourcePending.clear();
	// Their `settle` closures are inert now (the textures are gone), so the
	// counter must be zeroed here or it never drains.
	pendingFallbackLoads.clear();
	ktx2Loader?.dispose();
	ktx2Loader = null;
	// Drop the shared warm-up promise too, or a post-dispose remount would
	// resolve it to the just-disposed loader instead of re-importing.
	ktx2LoaderWarmup = null;
	// Invalidates any `prewarmCompressedTextureTranscoder`/
	// `configureCompressedTextureSupport` continuation still awaiting the
	// (uncancellable) dynamic import from a now-torn-down canvas — see
	// `materialLibraryGeneration`.
	materialLibraryGeneration += 1;
	textureLoader = null;
	materialLibraryInvalidate = null;
	// The next Canvas re-probes support; parked bindings referenced materials
	// that were just disposed, so drop them rather than flushing onto corpses.
	deferredSlotBindings.length = 0;
	interiorBindingQueue = [];
	compressedSupport = "unknown";
}

export const matWood = createLazyMaterial(() => {
	// Raw framing and battens need pale, seam-free spruce. The legacy dark
	// diffuse remains specific to treated wood and barrel surfaces.
	const material = createTexturedStandardMaterial(
		"framing",
		EXTERIOR_PLANK_TEXTURE_SLOTS,
		{
			color: MATERIAL_TEXTURE_PROFILES.framing.tint,
			envMapIntensity: MATERIAL_TEXTURE_PROFILES.framing.envMapIntensity,
			metalness: 0,
			roughness: 0.88,
			normalScale: new THREE.Vector2(0.18, 0.18),
		},
		undefined,
		{ repeat: [1, 2], rotation: 0, offset: [0, 0] },
	);
	// Continuous timber has no panel-cell darkening or invented cross grooves.
	applyTimberGrain(material);
	return material;
});

/**
 * Species restyling for interior wood surfaces.
 *
 * There is one aspen albedo on disk, so a species is expressed the way this
 * pipeline has always expressed wood identity: the profile `tint` multiplies
 * the albedo and the roughness sets the sheen (see the 2026-03-11
 * texture-swaps/profile-tint note). `asp` carries today's exact values, so the
 * default path renders frame-identical output.
 *
 * No `needsUpdate`: colour and roughness are uniforms, so changing them needs
 * no shader re-derivation — the viewer's `invalidate()` schedules the redraw.
 */
function makeSpeciesApplier(lookKey: "panelLook" | "benchLook") {
	return (material: THREE.Material, finishes: FinishConfig): void => {
		const look = INTERIOR_WOOD_CATALOG[finishes.interiorWood][lookKey];
		const standard = material as THREE.MeshStandardMaterial;
		standard.color.setHex(look.tint);
		standard.roughness = look.roughness;
	};
}

const applyPanelSpecies = makeSpeciesApplier("panelLook");
const applyBenchSpecies = makeSpeciesApplier("benchLook");

/**
 * Facade treatment tinting.
 *
 * `untreated` has no look of its own: each material restores ITS OWN tint,
 * because the trim family sits deliberately lighter/darker than the cladding
 * and stamping one paint chip on all of them flattens the facade into a single
 * colour swatch.
 */
function makeTreatmentApplier(
	untreatedTint: number,
	untreatedRoughness: number,
) {
	return (material: THREE.Material, finishes: FinishConfig): void => {
		const look = EXTERIOR_TREATMENT_CATALOG[finishes.exteriorTreatment].look;
		const standard = material as THREE.MeshStandardMaterial;
		standard.color.setHex(look?.tint ?? untreatedTint);
		standard.roughness = look?.roughness ?? untreatedRoughness;
	};
}

export const matPanel = createLazyMaterial(
	() =>
		createTexturedWoodMaterial("panel", PANEL_TEXTURE_SLOTS, {
			metalness: 0.025,
			envMapIntensity: MATERIAL_TEXTURE_PROFILES.panel.envMapIntensity * 1.08,
		}),
	applyPanelSpecies,
);

/**
 * `matPanel` for boards that run along the mesh's U axis instead of its V
 * axis — the inner ceiling panel and the taklist/bomlist that trims it.
 *
 * The aspen albedo is a STANDING-plank sheet: its seams are lines of
 * constant u, so with the identity transform the boards run along V. On a
 * `BoxGeometry` top face V is world Z, which is exactly where the takgles
 * now runs (issue 115) — panel parallel to its own battens. Rotating the
 * maps 90° puts the plank axis along U, i.e. across the room (world X on
 * the ceiling, along the board on the vertical face of a lying trim list).
 *
 * The repeat is NOT swapped: Three's `setUvTransform` scales the ROTATED
 * coordinates, so `repeat.x` keeps addressing the plank axis at 90° (the
 * same reason `HORIZONTAL_BOARD_TEXTURE_TRANSFORM` keeps its repeat).
 * `flatBoardAxis: "x"` moves the procedural grain's fast axis with it so
 * the ticking still varies ACROSS the board, not along it.
 */
const CEILING_PANEL_TEXTURE_TRANSFORM: TextureTransform = {
	rotation: Math.PI / 2,
	repeat: MATERIAL_TEXTURE_PROFILES.panel.repeat,
	offset: [0, 0],
};

export const matCeilingPanel = createLazyMaterial(
	() =>
		createTexturedWoodMaterial(
			"panel",
			PANEL_TEXTURE_SLOTS,
			{
				metalness: 0.025,
				envMapIntensity: MATERIAL_TEXTURE_PROFILES.panel.envMapIntensity * 1.08,
			},
			{
				transform: CEILING_PANEL_TEXTURE_TRANSFORM,
				variation: { flatBoardAxis: "x" },
			},
		),
	applyPanelSpecies,
);

export const matPanelPhysical = createLazyMaterial(
	() =>
		createTexturedPhysicalWoodMaterial("panel", PANEL_TEXTURE_SLOTS, {
			metalness: 0.025,
			envMapIntensity: MATERIAL_TEXTURE_PROFILES.panel.envMapIntensity * 1.08,
			anisotropy: 0.4,
			anisotropyRotation: 0,
		}),
	applyPanelSpecies,
);

/**
 * Presentation-tier twin of `matCeilingPanel`. Without it the ceiling
 * would drop out of the standard→physical wood swap and read as a
 * different material from the walls in cinematic mode.
 */
export const matCeilingPanelPhysical = createLazyMaterial(
	() =>
		createTexturedPhysicalWoodMaterial(
			"panel",
			PANEL_TEXTURE_SLOTS,
			{
				metalness: 0.025,
				envMapIntensity: MATERIAL_TEXTURE_PROFILES.panel.envMapIntensity * 1.08,
				anisotropy: 0.4,
				anisotropyRotation: Math.PI / 2,
			},
			{
				transform: CEILING_PANEL_TEXTURE_TRANSFORM,
				variation: { flatBoardAxis: "x" },
			},
		),
	applyPanelSpecies,
);

/**
 * The rotated-grain panel material, under the name a LYING wall panel uses
 * (issue 134).
 *
 * Deliberately the SAME instance as the ceiling's, not a clone: a horizontal
 * wall panel and the inner ceiling are the same surface — bastupanel whose
 * boards run along the mesh's U axis — so they share one texture cache entry,
 * one shader program and one set of tuned parameters. Cloning them would put a
 * batching boundary and a silent drift seam exactly where the design wants the
 * two to read as one material.
 */
export const matPanelLying = matCeilingPanel;
export const matPanelLyingPhysical = matCeilingPanelPhysical;

// Both aspen albedo and the shared normal atlas have uninterrupted wood here.
// A lav slat is one board: atlas seams must not invent joints across its top.
const BENCH_TEXTURE_TRANSFORM: TextureTransform = {
	repeat: MATERIAL_TEXTURE_PROFILES.bench.repeat,
	rotation: 0,
	offset: [0.075, 0],
};

function createBenchMaterial(
	physical: true,
	species?: InteriorWoodSpecies,
): THREE.MeshPhysicalMaterial;
function createBenchMaterial(
	physical: false,
	species?: InteriorWoodSpecies,
): THREE.MeshStandardMaterial;
function createBenchMaterial(physical: boolean, species?: InteriorWoodSpecies) {
	const apply = species
		? (material: THREE.MeshStandardMaterial, finishes: FinishConfig) =>
				applyBenchSpecies(material, { ...finishes, interiorWood: species })
		: applyBenchSpecies;
	return createLazyMaterial(
		() =>
			physical
				? createTexturedPhysicalWoodMaterial(
						"bench",
						BENCH_TEXTURE_SLOTS,
						{
							metalness: 0.03,
							envMapIntensity:
								MATERIAL_TEXTURE_PROFILES.bench.envMapIntensity * 1.06,
							clearcoat: 0.15,
							clearcoatRoughness: 0.6,
						},
						{ transform: BENCH_TEXTURE_TRANSFORM },
					)
				: createTexturedWoodMaterial(
						"bench",
						BENCH_TEXTURE_SLOTS,
						{
							metalness: 0.03,
							envMapIntensity:
								MATERIAL_TEXTURE_PROFILES.bench.envMapIntensity * 1.06,
						},
						{ transform: BENCH_TEXTURE_TRANSFORM },
					),
		apply,
	);
}

export const matBench = createBenchMaterial(false);
export const matBenchPhysical = createBenchMaterial(true);
// Bounded library-owned materials retain wood shaders and are released by library disposal.
const fixedBenchMaterials = {
	asp: [createBenchMaterial(false, "asp"), createBenchMaterial(true, "asp")],
	al: [createBenchMaterial(false, "al"), createBenchMaterial(true, "al")],
	gran: [createBenchMaterial(false, "gran"), createBenchMaterial(true, "gran")],
} as const;
export function getBenchMaterial(species?: InteriorWoodSpecies) {
	return species ? fixedBenchMaterials[species][0] : matBench;
}
export const matTreatedWood = createLazyMaterial(() =>
	createTexturedStandardMaterial("framing", FRAMING_TEXTURE_SLOTS, {
		roughness: 0.91,
		color: 0x637259,
		envMapIntensity: 0.22,
		normalScale: new THREE.Vector2(0.5, 0.5),
	}),
);

export const matDeckBoard = createLazyMaterial(() =>
	createTexturedStandardMaterial(
		"framing",
		FRAMING_TEXTURE_SLOTS,
		{
			roughness: 0.93,
			color: 0x7f8c69,
			envMapIntensity: 0.18,
			normalScale: new THREE.Vector2(0.42, 0.42),
		},
		{ strength: 0.14 },
	),
);

/**
 * The barrel's stave shell. It IS the facade — there is no cladding layer to
 * tint instead — so it takes the exterior treatment like the rectangular
 * engines' cladding does. Without that a tunnbastu is billed for tjärvitriol
 * in the BOM, the price, the guide and the timeline, and renders untreated.
 *
 * `DoubleSide` here rather than on a `.clone()` at the call site: a clone is
 * detached from `applyFinishSelection`, so changing the colour would update a
 * material no mesh is using. (Treatment changes deliberately do NOT dirty the
 * geometry, so nothing would ever rebuild the clone either.)
 */
export const matStave = createLazyMaterial(
	() =>
		createTexturedWoodMaterial("stave", FRAMING_TEXTURE_SLOTS, {
			metalness: 0.02,
			side: THREE.DoubleSide,
		}),
	makeTreatmentApplier(
		MATERIAL_TEXTURE_PROFILES.stave.tint,
		MATERIAL_TEXTURE_PROFILES.stave.roughness,
	),
);

/**
 * The barrel's gable discs — also facade, so also treated, and also double-
 * sided (you see them from inside the barrel).
 *
 * Separate from `matEndCap`, which the barrel reuses for its INTERIOR floor
 * panel. One shared material for both would paint the floor you sit on with
 * the facade colour.
 */
export const matBarrelEndCap = createLazyMaterial(
	() =>
		createTexturedStandardMaterial("framing", FRAMING_TEXTURE_SLOTS, {
			roughness: 0.74,
			color: 0xc8a073,
			envMapIntensity: 0.52,
			normalScale: new THREE.Vector2(0.36, 0.36),
			side: THREE.DoubleSide,
		}),
	makeTreatmentApplier(0xc8a073, 0.74),
);

/** Interior floor panel of the barrel — NOT facade, so never treated. */
export const matEndCap = createLazyMaterial(() =>
	createTexturedStandardMaterial("framing", FRAMING_TEXTURE_SLOTS, {
		roughness: 0.74,
		color: 0xc8a073,
		envMapIntensity: 0.52,
		normalScale: new THREE.Vector2(0.36, 0.36),
	}),
);

export const matDivider = createLazyMaterial(() =>
	createTexturedStandardMaterial("panel", PANEL_TEXTURE_SLOTS, {
		roughness: 0.72,
		color: 0xe4c9a7,
		envMapIntensity: 0.48,
		normalScale: new THREE.Vector2(0.3, 0.5),
	}),
);

/**
 * Physically-based glazing — refraction via transmission. Any mesh
 * carrying a transmissive material makes three.js render the whole
 * scene one extra time per frame, so the viewer only assigns this on
 * profiles with `physicalGlassEnabled` (explicit cinematic mode);
 * everywhere else `applyGlassQualityMode` swaps glass meshes to
 * `matGlassBudget`. Engines always build with this material.
 */
export const matGlass = createLazyMaterial(
	() =>
		new THREE.MeshPhysicalMaterial({
			color: 0xf5f8ff,
			metalness: 0,
			roughness: 0.05,
			transmission: 1,
			ior: 1.5,
			// Scene units are meters, so 20 mm glazing = 0.02. This is
			// the refraction volume three.js integrates through — it does
			// not need to match the pane geometry (0.02–0.035 m boxes).
			thickness: 0.02,
			attenuationColor: new THREE.Color(0xdde9f4),
			attenuationDistance: 2.2,
			// Bumped from 1.05 to take advantage of the real HDRI on
			// balanced/high tiers — reflections on the glazing are the
			// strongest premium-cue on this surface.
			envMapIntensity: 1.18,
			specularIntensity: 1,
			// No `reflectivity` here: on MeshPhysicalMaterial it is a
			// computed alias over `ior`, and passing both lets whichever
			// param lands last win — the previous reflectivity: 0.72 was
			// silently re-deriving ior to ~1.81. Window glass is 1.5.
			transparent: true,
		}),
);

/**
 * Opacity-glass fallback for tiers without `physicalGlassEnabled`.
 * No transmission → no extra render pass; alpha blending plus the
 * same fresnel/envMap tuning keeps the glazing reading as glass.
 */
export const matGlassBudget = createLazyMaterial(() => {
	const material = new THREE.MeshPhysicalMaterial({
		color: 0xf5f8ff,
		metalness: 0,
		roughness: 0.05,
		transmission: 0,
		ior: 1.5,
		envMapIntensity: 1.18,
		specularIntensity: 1,
		transparent: true,
		opacity: 0.08,
		depthWrite: false,
	});
	applyGlazingFresnel(material);
	return material;
});

/**
 * Cast concrete for foundation plinths and deck piers.
 *
 * The base colour used to be 0xa8afb6 — a light blue-grey that, with no
 * maps and no shader variation, read as white PVC pipe under the key
 * light. Cured concrete in daylight is both darker and warmer than that;
 * `applyConcreteVariation` supplies the mottling, aggregate speckle and
 * the ground-contact soiling that stop the surface collapsing to one
 * valör. See concreteVariation.ts.
 */
export const matConcrete = createLazyMaterial(() => {
	// 0x8d8f8a with env 0.62 still read as pale PVC at dusk: the sky IBL
	// lit the vertical shafts blue-white while the ground went dark. Cast
	// concrete sits near 0.3 albedo and is warmer; the lower env share
	// keeps it from out-shining the terrain it stands in.
	const material = new THREE.MeshStandardMaterial({
		color: 0x7f7d76,
		roughness: 0.95,
		metalness: 0,
		envMapIntensity: 0.46,
	});
	applyConcreteVariation(material);
	return material;
});

export const matMetal = createLazyMaterial(
	() =>
		new THREE.MeshPhysicalMaterial({
			color: 0xd2d5da,
			metalness: 0.9,
			roughness: 0.22,
			envMapIntensity: 1.1,
			// Clearcoat bumped from 0.18 → 0.32 so door handles and hinges
			// pick up a tighter specular streak under the real HDRI. Reads
			// as machined hardware rather than dull cast metal.
			clearcoat: 0.32,
			clearcoatRoughness: 0.22,
		}),
);

export const matHeater = createLazyMaterial(
	() =>
		new THREE.MeshPhysicalMaterial({
			color: 0x242629,
			roughness: 0.72,
			metalness: 0.35,
			envMapIntensity: 0.82,
			clearcoat: 0.08,
			clearcoatRoughness: 0.42,
		}),
);

/** Brushed stainless cage, owned by the shared material lifecycle. */
export const matStainlessCage = createLazyMaterial(
	() =>
		new THREE.MeshStandardMaterial({
			color: 0x92999c,
			metalness: 0.85,
			roughness: 0.48,
		}),
);

/** Dark split sauna stones; per-stone vertex color gives quiet variation. */
export const matHeaterStone = createLazyMaterial(
	() =>
		new THREE.MeshStandardMaterial({
			color: 0x555956,
			vertexColors: true,
			roughness: 0.94,
			metalness: 0.02,
			// Warmer rim color (0x120a04 → 0x1e1106) and slightly
			// brighter day baseline so the basket reads as "embered,
			// resting" rather than "off". The animation in
			// SaunaModel.tsx still drives the evening pulse on top.
			emissive: 0x1e1106,
			emissiveIntensity: 0.09,
		}),
);

/** Dark iron fire door with subtle warm emissive glow. */
export const matHeaterDoor = createLazyMaterial(
	() =>
		new THREE.MeshStandardMaterial({
			color: 0x1a1a1e,
			roughness: 0.62,
			metalness: 0.55,
			emissive: 0x4a1800,
			emissiveIntensity: 0.24,
		}),
);

/**
 * Heater fire-door glass (tinted, warm-lit). Moved here from
 * heaterGeometry.ts so it joins the lazy/disposable library and the
 * physical↔budget glass pairing below. The `-door-glass` meshes
 * using it are the heater-glow emissive targets in SaunaModel.tsx —
 * both variants must keep the warm emissive so the fire reads
 * through the door on every tier.
 */
export const matFireGlass = createLazyMaterial(
	() =>
		new THREE.MeshPhysicalMaterial({
			color: 0x1a0800,
			metalness: 0,
			roughness: 0.05,
			transmission: 0.7,
			ior: 1.5,
			thickness: 0.004,
			transparent: true,
			emissive: 0xff4400,
			// Bumped from 0.25 — the resting baseline used to be lost in day
			// mode (key + ambient + sky fill drowned the warm tint). 0.45 reads
			// as an unmistakable "there's fire in there" through the glass even
			// before the per-frame reset in SaunaModel.tsx runs.
			emissiveIntensity: 0.45,
			attenuationColor: new THREE.Color(0xffb07a),
			attenuationDistance: 1.4,
			envMapIntensity: 0.96,
		}),
);

/**
 * Budget fire-door glass: the dark tint and warm emissive carry the
 * "fire inside" read on their own, so dropping transmission costs
 * almost nothing visually while removing the pass entirely on
 * low/balanced (the fire door alone would otherwise trigger it).
 */
export const matFireGlassBudget = createLazyMaterial(
	() =>
		new THREE.MeshPhysicalMaterial({
			color: 0x1a0800,
			metalness: 0,
			roughness: 0.05,
			transmission: 0,
			ior: 1.5,
			transparent: true,
			opacity: 0.92,
			emissive: 0xff4400,
			emissiveIntensity: 0.45,
			envMapIntensity: 0.96,
		}),
);

/** Brushed stainless steel for electric heater tower. */
export const matHeaterElectric = createLazyMaterial(
	() =>
		new THREE.MeshPhysicalMaterial({
			color: 0xc8cdd4,
			roughness: 0.26,
			metalness: 0.82,
			envMapIntensity: 1.12,
			anisotropy: 0.28,
		}),
);

/** Chimney / flue pipe — matte black painted steel (typical Schiedel / Bastuforum finish). */
export const matChimney = createLazyMaterial(
	() =>
		new THREE.MeshPhysicalMaterial({
			color: 0x1e1e22,
			metalness: 0.55,
			roughness: 0.48,
			envMapIntensity: 0.88,
			// Painted-steel flue: bumped clearcoat to 0.18 so the pipe
			// reads as freshly-painted under sun, not chalky.
			clearcoat: 0.18,
			clearcoatRoughness: 0.4,
		}),
);

/** Roof flashing / storm collar — galvanized or painted sheet metal for weather-tightness. */
export const matRoofFlashing = createLazyMaterial(
	() =>
		new THREE.MeshPhysicalMaterial({
			color: 0x3a3a40,
			metalness: 0.78,
			roughness: 0.22,
			envMapIntensity: 1.18,
			// Bumped from 0.22 → 0.35 to give the storm collar / drip
			// edge a crisper highlight when the sun catches it. The
			// clearcoat stays narrow (clearcoatRoughness 0.24) so we get
			// a streak, not a flood.
			clearcoat: 0.35,
			clearcoatRoughness: 0.24,
		}),
);

export const matShield = createLazyMaterial(
	() =>
		new THREE.MeshStandardMaterial({
			color: 0xd6dbe0,
			roughness: 0.58,
			metalness: 0,
		}),
);

export const matRoofTop = createLazyMaterial(() =>
	createTexturedWoodMaterial(
		"roof",
		{
			normalMap: FRAMING_TEXTURE_SLOTS.normalMap,
			roughnessMap: FRAMING_TEXTURE_SLOTS.roughnessMap,
		},
		{
			metalness: 0.12,
		},
	),
);

/**
 * Underlagspapp — only visible in the section view (#079) and at the
 * takfot, so the ambition is deliberately low. What it must NOT be is
 * indistinguishable from the ytpapp above it, or the two layers collapse
 * into one black band in the section. It is therefore lighter, smooth
 * (no mineral granulate — the surface layer owns that cue) and laid in
 * noticeably wider våder. See feltVariation.ts.
 */
export const matRoofUnderlay = createLazyMaterial(() => {
	const material = new THREE.MeshStandardMaterial({
		color: 0x33353b,
		roughness: 0.9,
		metalness: 0.04,
	});
	applyFeltVariation(material, {
		granuleFrequency: 90,
		granuleStrength: 0.06,
		granuleRoughness: 0.07,
		// Skips the <normal_fragment_maps> patch entirely — this layer is
		// almost never lit directly, so the jitter would be pure cost.
		granuleNormalStrength: 0,
		courseSpacingM: 1.35,
		seamWidth: 0.04,
		seamStrength: 0.2,
		seamHighlight: 0.05,
		courseToneVariation: 0.03,
	});
	return material;
});

/**
 * Ytpapp — the visible roof covering on `felt_roof` systems. Flat and
 * mapless it read as a matte rubber sheet; the granulate and the
 * overlapping våder are what make it read as takpapp from above.
 * See feltVariation.ts.
 */
export const matRoofFelt = createLazyMaterial(() => {
	const material = new THREE.MeshStandardMaterial({
		color: 0x1e2126,
		roughness: 0.94,
		metalness: 0.02,
		envMapIntensity: 0.7,
	});
	applyFeltVariation(material);
	return material;
});

export const matRoofSheetMetal = createLazyMaterial(
	() =>
		new THREE.MeshStandardMaterial({
			color: 0x87919d,
			roughness: 0.34,
			metalness: 0.82,
		}),
);

export const matBand = createLazyMaterial(
	() =>
		new THREE.MeshStandardMaterial({
			color: 0x525961,
			metalness: 0.84,
			roughness: 0.32,
		}),
);

export const matCradle = createLazyMaterial(() =>
	createTexturedStandardMaterial("framing", FRAMING_TEXTURE_SLOTS, {
		roughness: 0.84,
		color: 0x9a7a52,
		envMapIntensity: 0.28,
		normalScale: new THREE.Vector2(0.44, 0.44),
	}),
);

/* ─── Ventilation: real hardware vs. diagram colours ──────────────────
 *
 * A vent on a wood facade is a tallriksventil — a white/brushed metal
 * plate valve. That is what the engines build, and it is what the photo
 * view, the PDF snapshots and shared designs show.
 *
 * The blue/red supply/exhaust coding is an ANALYSIS convention. It is
 * applied by the view layer (`applyVentilationDiagramMode`), which swaps
 * the face materials of vent meshes when the ventilation overlay is on.
 * Never assign `matVentIn`/`matVentOut` in an engine — a diagram colour
 * baked into the geometry leaks into every export.
 *
 * Supply and exhaust hardware get SEPARATE (visually identical) neutral
 * materials so the diagram swap can be keyed on material identity rather
 * than mesh names — the same axis `sectionLayers` classifies on.
 */
function createVentFaceMaterial(): THREE.MeshStandardMaterial {
	// Powder-coated white plate valve — matt, faintly metallic, so it
	// reads as hardware against the spruce cladding without glinting.
	return new THREE.MeshStandardMaterial({
		color: 0xece9e3,
		roughness: 0.42,
		metalness: 0.22,
		envMapIntensity: 0.5,
	});
}

/** Neutral plate-valve face for a fresh-air intake (photo view). */
export const matVentSupplyFace = createLazyMaterial(createVentFaceMaterial);

/** Neutral plate-valve face for an exhaust vent (photo view). */
export const matVentExhaustFace = createLazyMaterial(createVentFaceMaterial);

/**
 * Shadow gap between the flange and the valve plate — the dark ring that
 * makes a plate valve read as an opening rather than a disc on a wall.
 * Never swapped: the recess stays dark in both modes.
 */
export const matVentThroat = createLazyMaterial(
	() =>
		new THREE.MeshStandardMaterial({
			color: 0x24262a,
			roughness: 0.9,
			metalness: 0.05,
		}),
);

/** Analysis-mode supply colour (blue). Overlay-owned — see the note above. */
export const matVentIn = createLazyMaterial(
	() =>
		new THREE.MeshStandardMaterial({
			color: 0x3b82f6,
			roughness: 0.5,
			metalness: 0.08,
			emissive: 0x13365d,
			emissiveIntensity: 0.26,
		}),
);

/** Analysis-mode exhaust colour (red). Overlay-owned — see the note above. */
export const matVentOut = createLazyMaterial(
	() =>
		new THREE.MeshStandardMaterial({
			color: 0xef4444,
			roughness: 0.5,
			metalness: 0.08,
			emissive: 0x5a1616,
			emissiveIntensity: 0.24,
		}),
);

/**
 * A material the viewer swaps between two variants of the same surface:
 * `[off, on]`, where `on` is chosen when the mode's boolean is true.
 * Entries hold the lazy proxies, so listing them instantiates nothing.
 */
type MaterialModePair = readonly [off: THREE.Material, on: THREE.Material];

/**
 * Swap every mesh under `root` to one side of each material pair. Mesh
 * names and geometry are untouched, so audit contracts, BOM parity and
 * visibility gating are unaffected. Returns true when something changed,
 * so callers can skip invalidation (and any follow-up work) on a no-op.
 *
 * This is the one seam for "same geometry, different material" view modes:
 * glass quality, wood surface quality and the ventilation diagram all ride
 * it. Engines always build the `off` side.
 */
function applyMaterialModePairs(
	root: THREE.Object3D,
	pairs: readonly MaterialModePair[],
	modeEnabled: boolean,
): boolean {
	let changed = false;
	root.traverse((child) => {
		if (!(child instanceof THREE.Mesh)) return;
		const material = child.material;
		if (Array.isArray(material)) return;
		for (const [off, on] of pairs) {
			const target = modeEnabled ? on : off;
			if (material === target) return;
			if (material === off || material === on) {
				child.material = target;
				changed = true;
				return;
			}
		}
	});
	return changed;
}

/** Neutral hardware ↔ blue/red supply/exhaust diagram faces. */
const VENT_DIAGRAM_MATERIAL_PAIRS: readonly MaterialModePair[] = [
	[matVentSupplyFace, matVentIn],
	[matVentExhaustFace, matVentOut],
];

/**
 * Swap vent faces under `root` between the neutral hardware look and the
 * blue/red supply/exhaust diagram colours.
 */
export function applyVentilationDiagramMode(
	root: THREE.Object3D,
	diagramEnabled: boolean,
): boolean {
	return applyMaterialModePairs(
		root,
		VENT_DIAGRAM_MATERIAL_PAIRS,
		diagramEnabled,
	);
}

export const matGround = createLazyMaterial(() => {
	// Ground is fully procedural now — diffuse, roughness, and base
	// color all come from `applyGroundVariation`'s shader patch (3
	// octaves of world-space value noise). No diffuse / normal /
	// roughness textures, so no UV repeat is visible at any camera
	// angle. The base `color` only acts as a fallback if the shader
	// patch ever fails to apply; the shader overwrites diffuseColor.
	const material = new THREE.MeshStandardMaterial({
		color: MATERIAL_TEXTURE_PROFILES.ground.tint,
		roughness: MATERIAL_TEXTURE_PROFILES.ground.roughness,
		metalness: 0,
		envMapIntensity: MATERIAL_TEXTURE_PROFILES.ground.envMapIntensity,
	});
	applyGroundVariation(material);
	return material;
});

// Opening-edit guides sit on top of the actual wall finish, so they
// need a polygonOffset bias to win the depth comparison at every
// camera angle. Without it the blue stud frame and the wall panel
// fight pixel-for-pixel and the frame flickers as the camera moves.
const GUIDE_POLYGON_OFFSET_FACTOR = -2;
const GUIDE_POLYGON_OFFSET_UNITS = -2;

export const matGuideWireframe = createLazyMaterial(
	() =>
		new THREE.MeshBasicMaterial({
			color: 0xf97316,
			wireframe: true,
			polygonOffset: true,
			polygonOffsetFactor: GUIDE_POLYGON_OFFSET_FACTOR,
			polygonOffsetUnits: GUIDE_POLYGON_OFFSET_UNITS,
		}),
);

export const matGuideStud = createLazyMaterial(
	() =>
		new THREE.MeshBasicMaterial({
			color: 0x1d4ed8,
			polygonOffset: true,
			polygonOffsetFactor: GUIDE_POLYGON_OFFSET_FACTOR,
			polygonOffsetUnits: GUIDE_POLYGON_OFFSET_UNITS,
		}),
);

export const matGuideDoor = createLazyMaterial(
	() =>
		new THREE.MeshBasicMaterial({
			color: 0x4a3728,
			transparent: true,
			opacity: 0.7,
			polygonOffset: true,
			polygonOffsetFactor: GUIDE_POLYGON_OFFSET_FACTOR,
			polygonOffsetUnits: GUIDE_POLYGON_OFFSET_UNITS,
		}),
);

// Brushed-stainless door handle — first close-up touchpoint, so worth
// the upgrade to MeshPhysicalMaterial. The clearcoat gives a soft
// secondary highlight that reads as "polished metal" rather than
// "matte plastic" under the real HDRI.
export const matDoorHandle = createLazyMaterial(
	() =>
		new THREE.MeshPhysicalMaterial({
			color: 0xadb3ba,
			metalness: 0.82,
			roughness: 0.24,
			envMapIntensity: 1.22,
			clearcoat: 0.55,
			clearcoatRoughness: 0.18,
		}),
);

export const matWindBarrier = createLazyMaterial(
	() =>
		new THREE.MeshStandardMaterial({
			color: 0x25272b,
			roughness: 0.96,
			metalness: 0,
			side: THREE.DoubleSide,
		}),
);

/**
 * Trossbottenskiva — 12 mm asfaboard (asfaltimpregnerad porös träfiberskiva)
 * resting on the trossbottenläkt inside the floor-joist bays. It gets its OWN
 * material rather than reusing `matWindBarrier` (the wall's black wind-barrier
 * sheet) or `matPanel` (planed spruce): the whole point of issue "materialet
 * under bastun" is that the underside of the floor was reading as a SECOND
 * layer of wood boards. A dark, warm, matte brown-black — real asfaboard is
 * bitumen-brown, not the wind barrier's cool charcoal — makes the bay bottom
 * read as sheathing board at a glance. No maps: it is only ever seen from
 * below/in section with the golvspont hidden, so a flat matte surface is
 * enough and costs no texture memory.
 */
export const matAsphaltBoard = createLazyMaterial(
	() =>
		new THREE.MeshStandardMaterial({
			color: 0x33291f,
			roughness: 0.98,
			metalness: 0,
		}),
);

/**
 * Åldersbeständig bastufolie (aluminium vapour barrier) — a ≤2 mm plane
 * between the studs and the lying glespanel. Kept deliberately cheap: a
 * lightly metallic standard material, no PBR maps, so adding a full extra
 * wall layer costs nothing in texture memory. DoubleSide because the section
 * view cuts through it and it is thin enough to see the back face.
 */
export const matVaporBarrier = createLazyMaterial(
	() =>
		new THREE.MeshStandardMaterial({
			color: 0xb9bec4,
			roughness: 0.38,
			metalness: 0.75,
			side: THREE.DoubleSide,
		}),
);

/**
 * Stenullsisolering batts filling the stud bays. Matte yellow-brown mineral
 * wool — no maps, since it is only ever seen with the panel layer hidden
 * (framing / section view).
 */
export const matInsulation = createLazyMaterial(
	() =>
		new THREE.MeshStandardMaterial({
			color: 0xc9a86a,
			roughness: 1,
			metalness: 0,
		}),
);

export const matExteriorCladding = createLazyMaterial(
	() =>
		createTexturedWoodMaterial(
			"exterior",
			EXTERIOR_TEXTURE_SLOTS,
			{},
			{
				variation: {
					boardUvScale:
						MATERIAL_TEXTURE_PROFILES.exterior.repeat[0] *
						EXTERIOR_TEXTURE_PLANKS_PER_TILE,
					brightnessRange: 0.065,
					roughnessRange: 0.085,
					macroVariation: 0.015,
					// The texture owns seams; diagonal spatial grooves would cross the boards.
					grooveStrength: 0,
					grazingLift: 0.018,
					grainStrength: 0.015,
					// Rough-sawn fibre relief and uneven wear: a painted
					// facade board catches the sky in streaks, not as one
					// flat sheet (most visible on black slamfärg at dusk).
					fibreStrength: 0.75,
					sheenVariation: 0.3,
				},
			},
		),
	makeTreatmentApplier(
		MATERIAL_TEXTURE_PROFILES.exterior.tint,
		MATERIAL_TEXTURE_PROFILES.exterior.roughness,
	),
);

// Corner boards (knutbrädor) and exterior door/window foder — both use this
// material, so they read as one consistent trim family. `envMapIntensity`
// used to be hardcoded to 0.34, well below the "exterior" wood profile's own
// default of 0.55 (see materialProfiles.ts) that `matExteriorCladding` picks
// up automatically. Even with a near-identical tint (0xd8cab4 vs the
// cladding's 0xd6cab8), that ~38% cut in environment-light contribution read
// as visibly darker/muddier trim under outdoor HDRI lighting — "dark brown"
// knutbrädor next to light cladding. Match the cladding's envMapIntensity so
// trim and cladding pick up the same amount of sky light.
/**
 * UV transform for STANDING trim boards (knutbrädor, foder jambs). Trim
 * uses the single-plank `exteriorPlank` crop — a knutbräda is ONE board,
 * and with the multi-plank exterior albedo a 90mm face squashes ~8 plank
 * seams into dense stripes (the 22mm edge reveal ~50×), which read as
 * dark brown strips. The crop keeps seams out of every mip level too —
 * UV-windowing the full sheet still bled seams back in on heavily
 * minified faces. U spans the plank once; V tiles the grain twice along
 * the board's height.
 */
const VERTICAL_BOARD_TEXTURE_TRANSFORM: TextureTransform = {
	rotation: 0,
	repeat: [1, 2],
	offset: [0, 0],
};

export const matCornerTrim = createLazyMaterial(
	() =>
		createTexturedStandardMaterial(
			"exterior",
			EXTERIOR_PLANK_TEXTURE_SLOTS,
			{
				roughness: 0.8,
				color: MATERIAL_TEXTURE_PROFILES.exterior.tint,
				envMapIntensity: MATERIAL_TEXTURE_PROFILES.exterior.envMapIntensity,
				normalScale: new THREE.Vector2(0.32, 0.32),
			},
			{ strength: 0.22 },
			VERTICAL_BOARD_TEXTURE_TRANSFORM,
		),
	makeTreatmentApplier(MATERIAL_TEXTURE_PROFILES.exterior.tint, 0.8),
);

/**
 * UV transform for LYING trim boards (window head/sill foder, drip caps,
 * base trim, fascia). Uses the single-plank `exteriorPlank` crop, rotated
 * 90° so the grain runs along the board instead of across it — with a
 * standing-plank texture a horizontal board reads as a strip of sauna
 * panel. The crop (rather than a UV window into the multi-plank sheet)
 * keeps plank seams out of every mip level; windowing left a dark/rusty
 * seam line on heavily minified faces such as the sockel's 22 mm top
 * ledge. repeat.y sets how many grain lengths run along the board.
 */
const HORIZONTAL_BOARD_TEXTURE_TRANSFORM: TextureTransform = {
	rotation: Math.PI / 2,
	repeat: [1, 2],
	offset: [0, 0],
};

/**
 * `matCornerTrim` for horizontal members — same tint/sheen family so the
 * foder frame reads as one material, but with the wood grain running
 * along the board instead of across it.
 */
export const matCornerTrimHorizontal = createLazyMaterial(
	() =>
		createTexturedStandardMaterial(
			"exterior",
			EXTERIOR_PLANK_TEXTURE_SLOTS,
			{
				roughness: 0.8,
				color: MATERIAL_TEXTURE_PROFILES.exterior.tint,
				envMapIntensity: MATERIAL_TEXTURE_PROFILES.exterior.envMapIntensity,
				normalScale: new THREE.Vector2(0.32, 0.32),
			},
			{ strength: 0.22 },
			HORIZONTAL_BOARD_TEXTURE_TRANSFORM,
		),
	makeTreatmentApplier(MATERIAL_TEXTURE_PROFILES.exterior.tint, 0.8),
);

/**
 * Warm, oiled-cedar door leaf. Richer and warmer than the light wall
 * cladding (so a closed door reads as a finished entry door rather than
 * just more siding) and distinct from the pale framing wood (`matWood`)
 * the door used before. Shares the exterior wood textures — only the
 * tint, sheen and bevel differ.
 */
export const matDoorWood = createLazyMaterial(() =>
	createTexturedStandardMaterial(
		"exterior",
		EXTERIOR_TEXTURE_SLOTS,
		{
			color: 0xb07a45,
			roughness: 0.6,
			metalness: 0.02,
			envMapIntensity: 0.42,
			normalScale: new THREE.Vector2(0.42, 0.42),
		},
		{ strength: 0.18 },
	),
);

/** Budget (opacity) ↔ physical (transmission) glazing. */
const GLASS_MATERIAL_PAIRS: readonly MaterialModePair[] = [
	[matGlassBudget, matGlass],
	[matFireGlassBudget, matFireGlass],
];

/** Standard ↔ physical shading for presentation-critical wood surfaces. */
const WOOD_SURFACE_MATERIAL_PAIRS: readonly MaterialModePair[] = [
	[matPanel, matPanelPhysical],
	// Also covers `matPanelLying` — it IS this material.
	[matCeilingPanel, matCeilingPanelPhysical],
	[matBench, matBenchPhysical],
	...Object.values(fixedBenchMaterials),
];

/**
 * Swap every glass mesh under `root` to the physical (transmission) or
 * budget (opacity) variant. Returns true if anything changed, so callers
 * can skip invalidation and heater-target re-collection when nothing moved.
 */
export function applyGlassQualityMode(
	root: THREE.Object3D,
	physicalGlassEnabled: boolean,
): boolean {
	return applyMaterialModePairs(
		root,
		GLASS_MATERIAL_PAIRS,
		physicalGlassEnabled,
	);
}

/**
 * Swap presentation-critical wood surfaces to high-tier physical
 * variants. Engines always build standard materials; the viewer opts
 * into physical shading only when the runtime profile can afford it.
 */
export function applyWoodSurfaceQualityMode(
	root: THREE.Object3D,
	physicalWoodSurfacesEnabled: boolean,
): boolean {
	return applyMaterialModePairs(
		root,
		WOOD_SURFACE_MATERIAL_PAIRS,
		physicalWoodSurfacesEnabled,
	);
}

export const matFascia = createLazyMaterial(
	() =>
		createTexturedStandardMaterial(
			"exterior",
			EXTERIOR_TEXTURE_SLOTS,
			{
				roughness: 0.8,
				color: 0xd0c0a8,
				envMapIntensity: 0.34,
				normalScale: new THREE.Vector2(0.34, 0.34),
			},
			{ strength: 0.22 },
		),
	makeTreatmentApplier(0xd0c0a8, 0.8),
);

/**
 * Fascia boards (takfotsbrädor/vindskivor) are long horizontal boards —
 * use the along-the-board grain transform so they read as one board
 * instead of a strip of vertical panel. `matFascia` (unrotated) remains
 * for the soffit panel, whose grain direction is fine as-is.
 */
export const matFasciaHorizontal = createLazyMaterial(
	() =>
		createTexturedStandardMaterial(
			"exterior",
			EXTERIOR_PLANK_TEXTURE_SLOTS,
			{
				roughness: 0.8,
				color: 0xd0c0a8,
				envMapIntensity: 0.34,
				normalScale: new THREE.Vector2(0.34, 0.34),
			},
			{ strength: 0.22 },
			HORIZONTAL_BOARD_TEXTURE_TRANSFORM,
		),
	makeTreatmentApplier(0xd0c0a8, 0.8),
);

/* ─── Section-view (cutaway) clipping support ─────────────────────────
 *
 * The section view (issue 079) clips the sauna body against a draggable
 * plane and caps the exposed cross-sections. These factories return FRESH
 * materials the SectionView component owns and disposes per activation —
 * they are NOT lazy singletons, because per-view `clippingPlanes` set on a
 * shared instance would leak into every other view (snapshot/PDF/etc.).
 * See docs/notes/2026-04-12-shared-threejs-material-libraries... .
 */

/**
 * Carry a material's shader patch across a `clone()`.
 *
 * `Material.clone()` delegates to `Material.copy()`, which assigns an
 * EXPLICIT list of fields (blending, side, stencil…, verified against
 * three r183.2). `onBeforeCompile` and `customProgramCacheKey` are not
 * on that list: both live as OWN properties shadowing prototype
 * defaults, so a clone silently reverts to the no-op prototype hook and
 * renders with no wood grain, no edge bevel, no procedural ground.
 *
 * The two must be carried TOGETHER. The default
 * `customProgramCacheKey()` returns `this.onBeforeCompile.toString()` —
 * the closure's SOURCE TEXT, which is identical for every closure the
 * same `applyX(...)` factory produces regardless of its parameters. So
 * copying only the hook would trade a silent patch loss for a silent
 * program collision: two differently-parameterised materials (a
 * 0.22-strength trim bevel and a 0.14-strength deckboard) sharing one
 * compiled program. That is exactly the hazard `applyEdgeBevel` and
 * `applyWoodVariation` install their own keys to avoid — see the
 * rationale in edgeBevel.ts.
 *
 * A source that patches `onBeforeCompile` without installing a key
 * (applyGroundVariation) still lands on the prototype default here, and
 * the clone's default reads the same hook text — so the clone inherits
 * the source's collision behaviour rather than a new one.
 */
function carryShaderPatch(source: THREE.Material, clone: THREE.Material): void {
	if (Object.hasOwn(source, "onBeforeCompile")) {
		clone.onBeforeCompile = source.onBeforeCompile;
	}
	if (Object.hasOwn(source, "customProgramCacheKey")) {
		clone.customProgramCacheKey = source.customProgramCacheKey;
	}
}

/**
 * Clone a (possibly proxied) shared material and apply the section clip
 * planes. `DoubleSide` makes the cut read as filled — you see the solid's
 * interior faces through the cut even on tiers without stencil caps, so
 * the section never looks hollow. `clipShadows` keeps cast shadows in sync
 * with the cut.
 */
export function cloneMaterialForSection(
	material: THREE.Material,
	planes: THREE.Plane[],
): THREE.Material {
	const clone = material.clone();
	carryShaderPatch(material, clone);
	clone.clippingPlanes = planes;
	clone.clipShadows = true;
	clone.side = THREE.DoubleSide;
	clone.needsUpdate = true;
	return clone;
}

/**
 * The two stencil-only meshes for one capped object (the classic
 * shadow-volume cap recipe): back faces increment the stencil, front
 * faces decrement it, so fragments left "inside" the solid at the cut
 * plane end up non-zero. Both are clipped by the same planes and write
 * neither colour nor depth.
 */
export function createSectionStencilMaterials(planes: THREE.Plane[]): {
	back: THREE.Material;
	front: THREE.Material;
} {
	const make = (side: THREE.Side, op: THREE.StencilOp) => {
		const mat = new THREE.MeshBasicMaterial();
		mat.depthWrite = false;
		mat.depthTest = false;
		mat.colorWrite = false;
		mat.stencilWrite = true;
		mat.stencilFunc = THREE.AlwaysStencilFunc;
		mat.side = side;
		mat.clippingPlanes = planes;
		mat.stencilFail = op;
		mat.stencilZFail = op;
		mat.stencilZPass = op;
		return mat;
	};
	return {
		back: make(THREE.BackSide, THREE.IncrementWrapStencilOp),
		front: make(THREE.FrontSide, THREE.DecrementWrapStencilOp),
	};
}

/**
 * Cap-fill material for one material family. Renders the family colour
 * only where the stencil buffer is non-zero (i.e. inside that family's
 * cross-section), then resets those stencil texels to 0 so the next
 * family's cap starts clean. The cap quad itself is NOT clipped.
 */
export function createSectionCapMaterial(color: number): THREE.Material {
	const material = new THREE.MeshStandardMaterial({
		color,
		metalness: 0,
		roughness: 0.82,
		side: THREE.DoubleSide,
		// Bias the cap toward the camera so it wins the depth test against
		// the clipped solid's edge instead of z-fighting it at the plane.
		polygonOffset: true,
		polygonOffsetFactor: -1,
		polygonOffsetUnits: -1,
		stencilWrite: true,
		stencilRef: 0,
		stencilFunc: THREE.NotEqualStencilFunc,
		stencilFail: THREE.ReplaceStencilOp,
		stencilZFail: THREE.ReplaceStencilOp,
		stencilZPass: THREE.ReplaceStencilOp,
	});
	return material;
}
